import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BoltIcon } from "../components/Bolts";
import { useCart, fmtPrice } from "../contexts/CartContext";
import { ArrowLeft, ShoppingBag, Package, Sparkles, Sun, Shield, Scissors, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ========================================================
   STICKERS LANDING — three clickable product tiles
   ======================================================== */
export const StickersLandingPage = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/sticker/config`).then((r) => setConfig(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading || !config) return <div className="min-h-[60vh] flex items-center justify-center text-storm-mist">Loading…</div>;

    const sizes = config.pack_sizes || [];

    const tiles = [
        {
            slug: "blind-bag",
            title: "Blind Bag",
            icon: <Package className="w-10 h-10" />,
            desc: "Mystery sticker pack — the contents are a surprise. Just pick your size, type, and series number.",
        },
        ...sizes.map((s) => ({
            slug: s.size.toLowerCase().replace(/\s+/g, "-"),
            title: s.size,
            icon: <Sparkles className="w-10 h-10" />,
            desc: `Pick your exact pack — starting at ${fmtPrice(s.regular_price_cents || 0)}.`,
        })),
    ];

    return (
        <div data-testid="stickers-landing">
            <section className="border-b border-white/10">
                <div className="container-x py-10 md:py-14">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl text-white leading-none">
                        <span className="text-storm-bolt">DangerSky</span> Stickers
                    </h1>
                    <p className="text-storm-mist mt-3 max-w-xl">Mystery blind bags or pick your exact theme — grab a pack and stick the world.</p>
                    <p className="text-storm-mist/70 text-xs mt-3">All stickers are approximately within ±1" of the listed size.</p>
                </div>
            </section>

            <section className="container-x py-10 md:py-14">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
                    {tiles.map((t) => (
                        <Link key={t.slug} to={`/stickers/${t.slug}`}
                            className="group border border-white/10 hover:border-storm-bolt/50 bg-gradient-to-br from-storm-navy to-storm-void p-8 flex flex-col transition-colors">
                            <div className="text-storm-bolt mb-5 group-hover:scale-110 transition-transform origin-left">{t.icon}</div>
                            <h2 className="font-heading text-2xl md:text-3xl uppercase text-white leading-none mb-3">{t.title}</h2>
                            <p className="text-storm-mist text-sm flex-1">{t.desc}</p>
                            <span className="text-storm-bolt text-xs uppercase tracking-[0.25em] mt-6 inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                View options <ArrowLeft className="w-3 h-3 rotate-180" />
                            </span>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
};


/* ========================================================
   BLIND BAG DETAIL PAGE
   ======================================================== */
export const BlindBagPage = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/sticker/config`).then((r) => setConfig(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading || !config) return <div className="container-x py-20 text-storm-mist">Loading…</div>;

    const blindSizes = config.blind_bag_sizes || [];
    const vis = config.section_visibility || {};

    return (
        <div data-testid="blind-bag-page">
            <section className="border-b border-white/10">
                <div className="container-x py-10 md:py-14">
                    <Link to="/stickers" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-6 hover:gap-3 transition-all">
                        <ArrowLeft className="w-4 h-4" /> Stickers
                    </Link>
                    <h1 className="text-4xl md:text-5xl text-white leading-none">
                        <span className="text-storm-bolt">Blind Bag</span> Stickers
                    </h1>
                    <p className="text-storm-mist mt-3 max-w-2xl">
                        It's a mystery! You pick the size and type — we pick the stickers. Every blind bag is a
                        random assortment from our full catalog. You never know what you're going to get until you open it.
                    </p>
                </div>
            </section>

            <section className="container-x py-10 md:py-14 space-y-14">
                {/* Regular blind bags */}
                {vis.regular_blind_bags !== false && (
                    <BlindBagSection type="regular" title="Regular Stickers"
                        desc="For indoor surfaces — phones, laptops, water bottles, notebooks, game consoles. Not designed for prolonged sun exposure."
                        icon={<Shield className="w-6 h-6" />}
                        sizes={blindSizes} priceKey="regular_price_cents" />
                )}

                {/* UV Resistant blind bags */}
                {vis.uv_blind_bags !== false && (
                    <BlindBagSection type="uv_resistant" title="UV Resistant Stickers"
                        desc="Built for the outdoors — cars, bumpers, windows, toolboxes, coolers. UV-resistant laminate means they won't fade in the sun, rain, or snow."
                        icon={<Sun className="w-6 h-6" />}
                        sizes={blindSizes} priceKey="uv_price_cents" />
                )}
            </section>
        </div>
    );
};

/* Blind bag section: pick series + size → add to cart */
const BlindBagSection = ({ type, title, desc, icon, sizes, priceKey }) => {
    const [sizeIdx, setSizeIdx] = useState(sizes.length === 1 ? "0" : "");
    const { addItem } = useCart();

    const chosenSize = sizeIdx !== "" ? sizes[parseInt(sizeIdx, 10)] : null;
    const typeLabel = type === "uv_resistant" ? "UV Resistant" : "Regular";
    const price = chosenSize ? (chosenSize[priceKey] || 0) : 0;

    const add = () => {
        if (!chosenSize) { toast.error("Pick a size."); return; }
        addItem({
            id: `blind-bag-${type}-${chosenSize.size}-${Date.now()}`,
            title: `Blind Bag (${typeLabel}) — ${chosenSize.size}`,
            priceCents: price,
            variant: `${typeLabel} / ${chosenSize.size}`,
            imageUrl: null,
        });
        toast.success("Blind bag added to cart!");
    };

    return (
        <div className="border border-white/10 bg-gradient-to-br from-storm-navy to-storm-void p-6 md:p-8 max-w-2xl">
            <div className="flex items-center gap-3 mb-2">
                <div className="text-storm-bolt">{icon}</div>
                <h2 className="font-heading text-2xl md:text-3xl uppercase text-white leading-none">{title}</h2>
            </div>
            <p className="text-storm-mist text-sm mb-6">{desc}</p>

            <div className="space-y-4">
                {sizes.length > 1 && (
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">Size</label>
                        <select value={sizeIdx} onChange={(e) => setSizeIdx(e.target.value)}
                            className="w-full bg-storm-void border border-white/10 text-white px-3 py-3 focus:outline-none focus:border-storm-bolt">
                            <option value="">Choose…</option>
                            {sizes.map((s, i) => (
                                <option key={i} value={i}>{s.size} — {fmtPrice(s[priceKey] || 0)}</option>
                            ))}
                        </select>
                    </div>
                )}

                {price > 0 && <div className="font-heading text-2xl text-storm-bolt">{fmtPrice(price)}</div>}

                <button onClick={add} disabled={!chosenSize}
                    className="btn-primary w-full justify-center disabled:opacity-50">
                    <ShoppingBag className="w-4 h-4" /> Add to cart
                </button>
            </div>
        </div>
    );
};


/* ========================================================
   THEME PACK DETAIL PAGE (e.g. /stickers/10-pack)
   ======================================================== */
export const ThemePackPage = () => {
    const { slug } = useParams();
    const [config, setConfig] = useState(null);
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/sticker/config`),
            axios.get(`${API}/sticker/packs`),
        ]).then(([cRes, pRes]) => {
            setConfig(cRes.data);
            setPacks(pRes.data || []);
        }).finally(() => setLoading(false));
    }, [slug]);

    if (loading || !config) return <div className="container-x py-20 text-storm-mist">Loading…</div>;

    const sizes = config.pack_sizes || [];
    const vis = config.section_visibility || {};
    const sizeConfig = sizes.find((s) => s.size.toLowerCase().replace(/\s+/g, "-") === slug);

    if (!sizeConfig) {
        return <div className="container-x py-20 text-storm-mist">Pack size not found. <Link to="/stickers" className="text-storm-bolt">Back to stickers</Link></div>;
    }

    const available = packs.filter((p) => p.size === sizeConfig.size);
    const regularPacks = available.filter((p) => p.sticker_type === "regular");
    const uvPacks = available.filter((p) => p.sticker_type === "uv_resistant");

    return (
        <div data-testid="theme-pack-page">
            <section className="border-b border-white/10">
                <div className="container-x py-10 md:py-14">
                    <Link to="/stickers" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-6 hover:gap-3 transition-all">
                        <ArrowLeft className="w-4 h-4" /> Stickers
                    </Link>
                    <h1 className="text-4xl md:text-5xl text-white leading-none">
                        <span className="text-storm-bolt">{sizeConfig.size}</span> Sticker Packs
                    </h1>
                    <p className="text-storm-mist mt-3 max-w-2xl">
                        Pick your exact pack — you choose the stickers you want.
                    </p>
                    <p className="text-storm-mist/70 text-xs mt-2">All stickers are approximately within ±1" of the listed size.</p>
                </div>
            </section>

            <section className="container-x py-10 md:py-14 space-y-14">
                {/* No-repeat collection promise */}
                <div className="max-w-2xl border-l-2 border-storm-bolt pl-5">
                    <h2 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-2">Collect Them All — No Repeats</h2>
                    <p className="text-storm-mist text-sm leading-relaxed">
                        Every numbered pack contains a completely unique set of stickers.
                        <strong className="text-white"> A higher-numbered pack will never contain stickers from an earlier one.</strong> Keep
                        buying new numbers to grow your collection without duplicates. Want a specific set again? Just reorder that number.
                    </p>
                </div>
                {/* Regular packs */}
                {vis.regular_packs !== false && (
                    <TypeSection type="regular" title="Regular Stickers"
                        desc="For indoor surfaces — phones, laptops, water bottles, notebooks, game consoles. Not designed for prolonged sun exposure."
                        icon={<Shield className="w-6 h-6" />}
                        sizeConfig={sizeConfig} packs={regularPacks} priceKey="regular_price_cents" />
                )}

                {/* UV Resistant packs */}
                {vis.uv_packs !== false && (
                    <TypeSection type="uv_resistant" title="UV Resistant Stickers"
                        desc="Built for the outdoors — cars, bumpers, windows, toolboxes, coolers. UV-resistant laminate means they won't fade in the sun, rain, or snow."
                        icon={<Sun className="w-6 h-6" />}
                        sizeConfig={sizeConfig} packs={uvPacks} priceKey="uv_price_cents" />
                )}
            </section>
        </div>
    );
};


/* ========================================================
   SHARED: Type section (theme packs) with add-to-cart
   ======================================================== */
const TypeSection = ({ type, title, desc, icon, sizeConfig, packs, priceKey }) => {
    const [packId, setPackId] = useState("");
    const { addItem } = useCart();

    const chosenPack = packs.find((p) => p.id === packId);
    const typeLabel = type === "uv_resistant" ? "UV Resistant" : "Regular";
    const price = sizeConfig[priceKey] || 0;

    const add = () => {
        if (!chosenPack) { toast.error("Pick a pack."); return; }
        addItem({
            id: `${chosenPack.id}-${type}`,
            title: `${chosenPack.name} (${typeLabel}) — ${sizeConfig.size}`,
            priceCents: price,
            variant: `${typeLabel} / ${chosenPack.name}${chosenPack.sticker_size ? ` / ~${chosenPack.sticker_size}` : ""}`,
            imageUrl: `${API}/catalog/image/${chosenPack.image_id}`,
        });
        toast.success("Added to cart!");
    };

    return (
        <div className="border border-white/10 bg-gradient-to-br from-storm-navy to-storm-void p-6 md:p-8 max-w-2xl">
            <div className="flex items-center gap-3 mb-2">
                <div className="text-storm-bolt">{icon}</div>
                <h2 className="font-heading text-2xl md:text-3xl uppercase text-white leading-none">{title}</h2>
            </div>
            <p className="text-storm-mist text-sm mb-1">{desc}</p>
            <p className="font-heading text-xl text-storm-bolt mb-5">{fmtPrice(price)} per pack</p>

            {packs.length === 0 ? (
                <p className="text-storm-mist/60 text-sm">No {typeLabel.toLowerCase()} packs available yet for this size.</p>
            ) : (
                <div className="space-y-4">
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">Pick a pack</label>
                        <select value={packId} onChange={(e) => setPackId(e.target.value)}
                            className="w-full bg-storm-void border border-white/10 text-white px-3 py-3 focus:outline-none focus:border-storm-bolt">
                            <option value="">Select a pack…</option>
                            {packs.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}{p.sticker_size ? ` (~${p.sticker_size})` : ""}</option>
                            ))}
                        </select>
                    </div>

                    {chosenPack && (
                        <div className="flex items-center gap-4">
                            <img src={`${API}/catalog/image/${chosenPack.image_id}`} alt="" className="w-20 h-20 object-cover border border-white/10" />
                            <div>
                                <div className="text-white text-sm">{chosenPack.name}</div>
                                {chosenPack.sticker_size && <div className="text-storm-mist text-xs">Approx. sticker size: ~{chosenPack.sticker_size}</div>}
                            </div>
                        </div>
                    )}

                    <button onClick={add} disabled={!chosenPack}
                        className="btn-primary w-full justify-center disabled:opacity-50">
                        <ShoppingBag className="w-4 h-4" /> Add to cart
                    </button>
                </div>
            )}
        </div>
    );
};


/* ========================================================
   SHARED: Preview grid — shows all available packs
   ======================================================== */
const PackPreviewGrid = ({ title, packs }) => {
    if (!packs || packs.length === 0) return null;
    return (
        <div>
            <h2 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">{title}</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {packs.map((p) => (
                    <div key={p.id} className="text-center">
                        <div className="aspect-square border border-white/10 overflow-hidden bg-storm-void mb-1">
                            <img src={`${API}/catalog/image/${p.image_id}`} alt={p.name} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-storm-mist text-[10px] leading-tight block">{p.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};


/* ========================================================
   VINYL DECALS LANDING — intro + category tiles
   ======================================================== */
export const DecalsPage = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/sticker/config`).then((r) => setConfig(r.data)).finally(() => setLoading(false));
    }, []);

    const categories = config?.decal_categories || [];

    return (
        <div data-testid="decals-page">
            <section className="border-b border-white/10">
                <div className="container-x py-10 md:py-14">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl text-white leading-none"><span className="text-storm-bolt">DangerSky</span> Vinyl Decals</h1>
                    <p className="text-storm-mist mt-3 max-w-2xl">
                        Precision-cut vinyl decals for cars, windows, laptops, tumblers, and just about anything smooth and
                        clean. Each design is die-cut from durable, weatherproof vinyl — no background, no border, just the shape.
                        They hold up against rain, sun, and car washes.
                    </p>
                    <div className="mt-8 max-w-3xl">
                        <h2 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-4">How to Apply Your Decal</h2>
                        <div className="grid sm:grid-cols-4 gap-5 text-sm text-storm-mist">
                            <div>
                                <span className="text-storm-bolt font-heading uppercase text-lg block mb-1">1. Clean</span>
                                Wipe the surface with rubbing alcohol or glass cleaner. No soap residue — it needs to be completely clean and dry. This is the most important step.
                            </div>
                            <div>
                                <span className="text-storm-bolt font-heading uppercase text-lg block mb-1">2. Position</span>
                                Hold the decal up to the surface before peeling. Use small pieces of tape to mark where you want it. Step back and make sure it's straight.
                            </div>
                            <div>
                                <span className="text-storm-bolt font-heading uppercase text-lg block mb-1">3. Peel & Apply</span>
                                Peel the backing paper off slowly. Press one edge down first, then smooth outward with a credit card or squeegee to push out air bubbles.
                            </div>
                            <div>
                                <span className="text-storm-bolt font-heading uppercase text-lg block mb-1">4. Transfer Tape</span>
                                If your decal has transfer tape on top, wait 5 minutes, then peel it off slowly at a sharp angle. If a letter lifts, press it back down and wait longer.
                            </div>
                        </div>
                        <p className="text-storm-mist/60 text-xs mt-4">Best applied above 50°F. For curved surfaces, use a heat gun or hair dryer to help the vinyl conform to the shape.</p>
                    </div>
                </div>
            </section>

            <section className="container-x py-10 md:py-14">
                <h2 className="font-heading uppercase tracking-wider text-storm-bolt text-sm mb-5">Browse by Category</h2>
                {loading ? (
                    <p className="text-storm-mist">Loading…</p>
                ) : categories.length === 0 ? (
                    <p className="text-storm-mist">No categories yet.</p>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
                        {categories.map((c) => (
                            <Link key={c} to={`/vinyl-decals/category/${encodeURIComponent(c)}`}
                                className="group border border-white/10 hover:border-storm-bolt/50 bg-gradient-to-br from-storm-navy to-storm-void p-8 flex flex-col transition-colors">
                                <div className="text-storm-bolt mb-4 group-hover:scale-110 transition-transform origin-left"><Scissors className="w-8 h-8" /></div>
                                <h3 className="font-heading text-xl md:text-2xl uppercase text-white leading-none mb-2">{c}</h3>
                                <span className="text-storm-bolt text-xs uppercase tracking-[0.25em] mt-auto pt-6 inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                                    View designs <ArrowLeft className="w-3 h-3 rotate-180" />
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

/* ========================================================
   VINYL DECALS CATEGORY — grid of designs
   ======================================================== */
export const DecalCategoryPage = () => {
    const { category } = useParams();
    const decodedCategory = decodeURIComponent(category);
    const [decals, setDecals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/sticker/decals`, { params: { category: decodedCategory } })
            .then((r) => setDecals(r.data || []))
            .finally(() => setLoading(false));
    }, [decodedCategory]);

    return (
        <div data-testid="decal-category-page">
            <section className="border-b border-white/10">
                <div className="container-x py-10 md:py-14">
                    <Link to="/vinyl-decals" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-6 hover:gap-3 transition-all">
                        <ArrowLeft className="w-4 h-4" /> Vinyl Decals
                    </Link>
                    <h1 className="text-4xl md:text-5xl text-white leading-none"><span className="text-storm-bolt">{decodedCategory}</span></h1>
                    <p className="text-storm-mist mt-3">Pick a design — choose your color and size on the next screen.</p>
                </div>
            </section>
            <section className="container-x py-10 md:py-14">
                {loading ? <p className="text-storm-mist">Loading…</p> : decals.length === 0 ? (
                    <p className="text-storm-mist">No decals in this category yet.</p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                        {decals.map((d) => (
                            <Link key={d.id} to={`/vinyl-decals/item/${d.id}`}
                                className="group border border-white/10 hover:border-storm-bolt/50 transition-colors overflow-hidden">
                                <div className="aspect-square overflow-hidden bg-storm-void">
                                    <img src={`${API}/catalog/image/${d.image_id}`} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="p-3 font-heading uppercase text-white text-sm tracking-wide">{d.name}</div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

/* ========================================================
   SINGLE DECAL DETAIL — color + size → add to cart
   ======================================================== */
export const DecalDetailPage = () => {
    const { id } = useParams();
    const [decal, setDecal] = useState(null);
    const [config, setConfig] = useState({ decal_colors: [], decal_sizes: [] });
    const [loading, setLoading] = useState(true);
    const [color, setColor] = useState("");
    const [sizeIdx, setSizeIdx] = useState("");
    const { addItem } = useCart();

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/sticker/decals`, { params: { include_hidden: false } }),
            axios.get(`${API}/sticker/config`),
        ]).then(([dRes, cRes]) => {
            setDecal((dRes.data || []).find((d) => d.id === id) || null);
            setConfig(cRes.data || { decal_colors: [], decal_sizes: [] });
        }).finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="container-x py-20 text-storm-mist">Loading…</div>;
    if (!decal) return <div className="container-x py-20 text-storm-mist">Decal not found. <Link to="/vinyl-decals" className="text-storm-bolt">Back to decals</Link></div>;

    const chosenSize = sizeIdx !== "" ? config.decal_sizes[parseInt(sizeIdx, 10)] : null;

    const add = () => {
        if (!color) { toast.error("Pick a color."); return; }
        if (!chosenSize) { toast.error("Pick a size."); return; }
        addItem({
            id: `${decal.id}-${color}-${chosenSize.size}`,
            title: `${decal.name} Decal — ${color}, ${chosenSize.size}`,
            priceCents: chosenSize.price_cents,
            variant: `${color} / ${chosenSize.size}`,
            imageUrl: `${API}/catalog/image/${decal.image_id}`,
        });
        toast.success("Added to cart.");
    };

    return (
        <div data-testid="decal-detail-page" className="container-x py-10 md:py-14">
            <Link to={decal.category ? `/vinyl-decals/category/${encodeURIComponent(decal.category)}` : "/vinyl-decals"} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-6 hover:gap-3 transition-all">
                <ArrowLeft className="w-4 h-4" /> {decal.category || "Vinyl Decals"}
            </Link>
            <div className="grid md:grid-cols-2 gap-8 max-w-3xl">
                <div className="aspect-square border border-white/10 overflow-hidden bg-storm-void">
                    <img src={`${API}/catalog/image/${decal.image_id}`} alt={decal.name} className="w-full h-full object-cover" />
                </div>
                <div className="space-y-5">
                    <h1 className="font-heading text-3xl md:text-4xl uppercase text-white">{decal.name}</h1>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">Color</label>
                        <select value={color} onChange={(e) => setColor(e.target.value)}
                            className="w-full bg-storm-void border border-white/10 text-white px-3 py-3 focus:outline-none focus:border-storm-bolt">
                            <option value="">Choose a color…</option>
                            {config.decal_colors.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">Size</label>
                        <select value={sizeIdx} onChange={(e) => setSizeIdx(e.target.value)}
                            className="w-full bg-storm-void border border-white/10 text-white px-3 py-3 focus:outline-none focus:border-storm-bolt">
                            <option value="">Choose a size…</option>
                            {config.decal_sizes.map((s, i) => <option key={i} value={i}>{s.size} — {fmtPrice(s.price_cents)}</option>)}
                        </select>
                    </div>

                    {chosenSize && <div className="font-heading text-3xl text-storm-bolt">{fmtPrice(chosenSize.price_cents)}</div>}

                    <button onClick={add} className="btn-primary w-full justify-center">
                        <ShoppingBag className="w-4 h-4" /> Add to cart
                    </button>
                </div>
            </div>
        </div>
    );
};
